import { Box, useBreakpointValue, VStack } from '@chakra-ui/react';
import { Grid } from '@time-line/components/common';
import { BlogCard } from 'components/blog/BlogCard';
import { BlogNewsletterSignup } from 'components/blog/BlogNewsletterSignup';
import { BlogPostJsonLd } from 'components/blog/BlogPostJsonLd';
import { DesktopContents } from 'components/blog/Contents/DesktopContents';
import { MobileStickyContents } from 'components/blog/Contents/MobileStickyContents';
import { Contributors } from 'components/blog/Contributors';
import { Disclaimer } from 'components/blog/Disclaimer';
import { Footnotes } from 'components/blog/Footnotes';
import { Hero } from 'components/blog/Hero';
import { KeyTakeaways } from 'components/blog/KeyTakeaways';
import { PodcastPlayer } from 'components/blog/PodcastPlayer';
import { Transcript } from 'components/blog/Transcript';
import { PageWrapper } from 'components/common/PageWrapper';
import {
  ArticleContainer,
  RichTextResolver,
} from 'components/common/RichTextResolver';
import { Seo } from 'components/common/Seo';
import { usePost } from 'hooks/usePost';
import { formatTranscript, getFootnotes, getH2s } from 'lib/sanity';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

export const Article = () => {
  const router = useRouter();
  const { slug } = router.query;
  const { data: post } = usePost(slug as string);

  const {
    seo,
    contributors,
    title,
    summary,
    heroImage,
    heroVideoUrl,
    body,
    categories,
    episode,
    platforms,
    publishedAt,
    updatedAt,
    keyTakeaways,
    disclaimer,
    type,
  } = post;

  const blogItemsPerRow = useBreakpointValue(
    { base: 1, sm: 2, xl: 3 },
    { fallback: 'xl' },
  );
  const transcript = episode?.transcript;
  const footnotes = getFootnotes(body);
  const contents = getH2s(body);
  const hasDisclaimer = !!disclaimer;
  const hasTranscript = !!transcript;
  const hasReferences = !!footnotes.length;
  const hasKeyTakeaways = !!keyTakeaways?.length;
  const hasContributors = !!contributors?.length;
  const showContents =
    contents.length > 1 ||
    hasDisclaimer ||
    hasReferences ||
    hasTranscript ||
    hasKeyTakeaways ||
    hasContributors;
  const isPodcast = type === 'timeline_podcast';
  const contributorsTitle = isPodcast ? 'Speakers' : 'Authors';

  const formattedTranscript = transcript && formatTranscript(transcript);

  // Smooth scrolling is only needed on blog posts (references)
  useEffect(() => {
    const htmlElement = document.documentElement;
    const previous = htmlElement.style.scrollBehavior;
    htmlElement.style.scrollBehavior = 'smooth';

    return () => {
      if (previous === '') {
        htmlElement.style.removeProperty('scroll-behavior');
      } else {
        htmlElement.style.scrollBehavior = previous;
      }

      window.scrollTo({
        top: 0,
        behavior: 'auto',
      });
    };
  }, []);

  return (
    <PageWrapper as="main" bgColor="gray.50">
      <Seo
        seo={{
          title: seo?.title || title,
          description: seo?.description || summary,
          image: seo?.image || heroImage || undefined,
        }}
        openGraph={{
          type: 'article',
          article: {
            publishedTime: publishedAt,
            modifiedTime: updatedAt,
          },
        }}
      />
      <BlogPostJsonLd post={post} />
      <Grid mt={{ lg: 'lg' }} gridColumnGap={0} mb="2xl">
        <Grid.Inner minH="100dvh">
          <Box
            gridColumn={{ base: 'span 12', lg: 'span 8' }}
            maxWidth="680px"
            width="100%"
            mx="auto"
            pr={{ base: 0, lg: 'sm' }}
          >
            <Box mb={{ base: 'm', lg: 'xl' }}>
              <Hero
                categories={categories}
                publishedAt={publishedAt}
                updatedAt={updatedAt}
                summary={summary}
                title={title}
                image={post.episode ? undefined : heroImage}
                videoUrl={post.episode ? undefined : heroVideoUrl}
              />
            </Box>

            {episode && (
              <Box mb={{ base: 'm', lg: 'xl' }}>
                <ArticleContainer>
                  <PodcastPlayer
                    audio={episode.audio}
                    episodeTitle={episode.episodeTitle}
                    link={episode.link}
                    platforms={platforms}
                    podcastTitle={episode.podcastTitle}
                    thumbnail={episode.thumbnail}
                  />
                </ArticleContainer>
              </Box>
            )}

            {showContents && (
              <MobileStickyContents
                contents={contents}
                hasDisclaimer={hasDisclaimer}
                hasReferences={hasReferences}
                hasKeyTakeaways={hasKeyTakeaways}
                hasTranscript={hasTranscript}
                hasContributors={hasContributors}
              />
            )}

            {keyTakeaways && <KeyTakeaways keyTakeaways={keyTakeaways} />}

            <Box>
              <Box mt="2xl">
                <RichTextResolver blocks={body} container={ArticleContainer} />
              </Box>

              {formattedTranscript && (
                <Box mt="2xl">
                  <ArticleContainer>
                    <Transcript transcript={formattedTranscript} />
                  </ArticleContainer>
                </Box>
              )}

              {contributors && (
                <Box mt="2xl" display={{ base: 'block', lg: 'none' }}>
                  <Contributors
                    title={contributorsTitle}
                    contributors={contributors}
                  />
                </Box>
              )}

              {hasReferences && (
                <Box mt="2xl">
                  <ArticleContainer>
                    <Footnotes footnotes={footnotes} />
                  </ArticleContainer>
                </Box>
              )}

              {hasDisclaimer && (
                <Box mt="2xl">
                  <ArticleContainer>
                    <Disclaimer text={disclaimer} />
                  </ArticleContainer>
                </Box>
              )}
            </Box>
          </Box>
          <Box
            as="aside"
            minHeight="100dvh"
            borderLeft="1px solid"
            borderColor="blackAlpha.250"
            display={{ base: 'none', lg: 'block' }}
            gridColumn={{ base: 'span 12', lg: 'span 4' }}
            pl="lg"
          >
            <BlogNewsletterSignup />
            <VStack
              mt="2xl"
              spacing="lg"
              alignItems="stretch"
              position={showContents ? 'sticky' : 'static'}
              sx={{
                top: 'calc(var(--header-height) + var(--timeline-space-3xs))',
              }}
            >
              {showContents && (
                <DesktopContents
                  contents={contents}
                  hasDisclaimer={hasDisclaimer}
                  hasReferences={hasReferences}
                  hasKeyTakeaways={hasKeyTakeaways}
                  hasTranscript={hasTranscript}
                />
              )}
              {hasContributors && contributors && (
                <Contributors
                  title={contributorsTitle}
                  contributors={contributors}
                />
              )}
            </VStack>
          </Box>
        </Grid.Inner>
      </Grid>

      {blogItemsPerRow && post.nextArticles.length >= blogItemsPerRow && (
        <Grid mb="2xl">
          <Grid.Inner>
            {post.nextArticles.slice(0, blogItemsPerRow).map((nextArticle) => (
              <Box
                key={nextArticle._id}
                gridColumn={{ base: 'span 12', sm: 'span 6', xl: 'span 4' }}
              >
                <BlogCard
                  type={nextArticle.type}
                  title={nextArticle.title}
                  image={nextArticle.heroImage}
                  videoUrl={nextArticle.heroVideoUrl}
                  slug={nextArticle.slug}
                  publishedAt={nextArticle.publishedAt}
                  categories={nextArticle.categories}
                />
              </Box>
            ))}
          </Grid.Inner>
        </Grid>
      )}
    </PageWrapper>
  );
};
